import { useMutation } from '@apollo/react-hooks';
import { debounce, Grid, Box } from '@material-ui/core';
import {
	Button,
	Option,
	Select,
	TextBlock
} from 'neon-design-system';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { CREATE_ACCOUNT_CHANNEL_SUBSCRIPTION } from '../../../gql/subscriptions';
import { ChannelSubscribeInput } from '../../../gql/users';
import { Channel, ChannelSubscriptionType } from '../../../shared/types';
import { ContactMarexEmail } from './ContactMarexEmail';
import { SelectOption } from './PremiumChannelPaymentScreen';
import TermsAndConditionsCheckbox from '../../ui/TermsAndConditionsCheckbox';
import { useFormControl, useFormGroup } from '../../../utils/form.utils';
import { booleanTrueValidator, requiredValidator } from '../../../utils/validator.utils';

interface PremiumChannelAccountPaymentProps {
	billingAccounts: Option[];
	channelId: string;
	fetchBillingAccounts: (filter: string, callback: (options: SelectOption[]) => void) => void;
	successUrl: string;
	channelName: string;
	goBack: () => void;
}

export const PremiumChannelAccountPayment = (props: PremiumChannelAccountPaymentProps) => {
	const { billingAccounts, channelId, successUrl, channelName, fetchBillingAccounts, goBack } = props;

	const form = useFormGroup({
		accountNumber: useFormControl<Option | null>(null, [requiredValidator]),
		acceptedTerms: useFormControl<boolean>(false, [booleanTrueValidator])
	});

	const history = useHistory();

	const [createAccountChannelSubscription] =
		useMutation<Channel, { item: ChannelSubscribeInput }>(CREATE_ACCOUNT_CHANNEL_SUBSCRIPTION, {
			onCompleted: () => {
				history.replace(successUrl);
			}
		});

	const onSubmit = async (ev: any) => {
		if (!channelId) return;

		ev?.preventDefault();
		form.setDirty();
		if (!form.isValid) {
			return;
		}

		return createAccountChannelSubscription({
			variables: {
				item: {
					channelId: channelId,
					subscriptionType: ChannelSubscriptionType.Account,
					accountNumber: form.value.accountNumber
				}
			}
		});

	}

	const debounceGetUser = debounce((filter, callback) => fetchBillingAccounts(filter, callback), 500);

	return (
		<Grid container spacing={4}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextBlock styleName="body">
							You will be charged every 1st of the month.
							This subscription will appear on your monthly statement as <b> Neon Insights - {channelName}</b>.
						</TextBlock>
					</Grid>
					<Grid item xs={12}>
						<Select
							id="clearing-account-select-id"
							label="Clearing account number"
							clearable
							size="large"
							placeholder="Select an account"
							error={form.accountNumber.showError}
							errorText={form.accountNumber.validationMessage}
							onBlur={form.accountNumber.handleBlur}
							value={form.accountNumber.value}
							onChange={(option: Option): void => {
								form.accountNumber.setValue(option);
							}}
							loadOptions={(inputValue: string, callback) => debounceGetUser(inputValue, callback)}
							defaultOptions={billingAccounts}
						/>
						<Box mt={1}>
							<TextBlock styleName="caption1" ink="muted">
								If the account is not listed, please
								contact <ContactMarexEmail /> and
								supply the correct account number.
							</TextBlock>
						</Box>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<TermsAndConditionsCheckbox
					acceptedTerms={form.acceptedTerms.value}
					setAcceptedTerms={(acceptedTerms: boolean) => form.acceptedTerms.setValue(acceptedTerms)}
					showError={form.acceptedTerms.showError}
				/>
			</Grid>
			<Grid item xs={12}>
				<Box display="flex">
					<Box mr={1}>
						<Button
							colorType="grey"
							onClick={goBack}
						>
							Back
						</Button>
					</Box>
					<Box>
						<Button
							colorType="primary"
							onClick={onSubmit}
						>
							Confirm
						</Button>
					</Box>
				</Box>
			</Grid>
		</Grid>
	);
}
